import React, { useMemo } from 'react';
import { normalizeJewelryOptionRow, sanitizeJewelryOptions } from './jewelryCustomization';

function ensureEditableRows(options) {
  const valid = sanitizeJewelryOptions(options);
  if (valid.length === 0) return [{ value: '', label: '', priceDelta: 0 }];
  const last = options?.[options.length - 1];
  if (last && !String(last.label || '').trim()) {
    return [...valid, { value: '', label: '', priceDelta: 0 }];
  }
  return valid;
}

/**
 * Simple row editor for chain / ring / bracelet choices (replaces pipe-delimited textarea).
 */
export default function JewelryOptionListEditor({
  options = [],
  onChange,
  showPrice = true,
  defaultOptions = null,
}) {
  const rows = useMemo(() => ensureEditableRows(options), [options]);

  const emit = (nextRows) => {
    onChange?.(nextRows);
  };

  const updateRow = (index, patch) => {
    const next = rows.map((row, i) => (i === index ? { ...row, ...patch } : row));
    emit(next);
  };

  const finalizeRow = (index) => {
    const next = rows.map((row, i) => (i === index ? normalizeJewelryOptionRow(row, i) : row));
    emit(next);
  };

  const removeRow = (index) => {
    const next = rows.filter((_, i) => i !== index);
    emit(next.length ? next : [{ value: '', label: '', priceDelta: 0 }]);
  };

  const addRow = () => {
    emit([...rows.map((row, i) => normalizeJewelryOptionRow(row, i)), { value: '', label: '', priceDelta: 0 }]);
  };

  const resetDefaults = () => {
    if (defaultOptions?.length) {
      emit(defaultOptions.map((o) => ({ ...o })));
    }
  };

  return (
    <div className="jewelry-option-list-editor">
      <div className="jewelry-option-list-editor__header">
        <span>What customers see</span>
        {showPrice ? <span>Price change (₹)</span> : null}
        <span className="visually-hidden">Remove</span>
      </div>
      {rows.map((row, index) => (
        <div key={`${index}-${row.value || 'new'}`} className="jewelry-option-list-editor__row">
          <input
            type="text"
            className="form-control form-control-sm"
            placeholder={showPrice ? 'e.g. 18″ or Pendant only (no chain)' : 'e.g. Size 7'}
            value={row.label}
            aria-label={`Choice ${index + 1} label`}
            onChange={(e) => updateRow(index, { label: e.target.value })}
            onBlur={() => finalizeRow(index)}
          />
          {showPrice ? (
            <input
              type="number"
              step="1"
              className="form-control form-control-sm jewelry-option-list-editor__price"
              value={Number.isFinite(Number(row.priceDelta)) ? row.priceDelta : 0}
              aria-label={`Choice ${index + 1} price change`}
              onChange={(e) => {
                const raw = e.target.value;
                updateRow(index, { priceDelta: raw === '' ? 0 : Number(raw) });
              }}
            />
          ) : null}
          <button
            type="button"
            className="btn btn-sm btn-outline-secondary jewelry-option-list-editor__remove"
            onClick={() => removeRow(index)}
            disabled={rows.length <= 1 && !String(row.label || '').trim()}
            aria-label={`Remove choice ${index + 1}`}
          >
            ×
          </button>
        </div>
      ))}
      <div className="jewelry-option-list-editor__actions">
        <button type="button" className="btn btn-sm btn-outline-primary" onClick={addRow}>
          + Add choice
        </button>
        {defaultOptions?.length ? (
          <button type="button" className="btn btn-sm btn-link text-muted px-0" onClick={resetDefaults}>
            Reset to store defaults
          </button>
        ) : null}
      </div>
      {showPrice ? (
        <p className="small text-muted mb-0 mt-2">
          Leave price at 0 for no change. Use negative numbers to reduce price (e.g. −100 for no chain).
        </p>
      ) : (
        <p className="small text-muted mb-0 mt-2">Customers pick from these labels at checkout.</p>
      )}
    </div>
  );
}
